import type {
	_EventSpecBase,
	AddedPaymentInfo,
	AddedToCart,
	AddedToWishlist,
	LineItem as ConvergeLineItem,
	LoggedIn,
	PlacedOrder,
	RemovedFromCart,
	RemovedFromWishlist,
	Searched,
	SearchedForStore,
	SelectedProduct,
	SignedUp,
	StartedCheckout,
	SubscribedToNewsletter,
	TrackArgs as TrackArgsBase,
	ViewedCart,
	ViewedCollection,
	ViewedPage,
	ViewedProduct
} from '@runconverge/browser'
import type { CartLine as ShopifyCartLine } from '@shopify/web-pixels-extension'
import type { AddToCartLine } from '~/actions/cart'
import type { LoadedCart } from '~/reducers/cart'
import type { Checkout, LineItem, Variant } from '~/types'
import { lineDiscount, toMoneyV2Numeric } from './cart'
import { gidToId } from './storefront'

export function toShopifyAnalyticsCartLine(
	line: AddToCartLine | VariantID,
	cart?: LoadedCart | Checkout
): ShopifyCartLine | null {
	const item = typeof line === 'string' ? cart?.lines.find(l => l.merchandise.id === line) : line
	const cartItem = cart?.lines.find(l => l.merchandise.id === item?.id)
	if (!item) return null
	const price =
		cartItem?.merchandise.compareAtPrice ?? cartItem?.merchandise.price ?? ('price' in item ? item.price : null)
	if (!price) return null
	return {
		quantity: item.quantity,
		cost: {
			totalAmount: toMoneyV2Numeric(price)
		},
		merchandise: {
			...item,
			...cartItem?.merchandise,
			sku: null,
			untranslatedTitle: cartItem?.merchandise?.title ?? null,
			image: 'image' in item && item.image ? { src: item.image.url } : null
		} as any
	}
}

export function toConvergeCartLine(item: LineItem): ConvergeLineItem {
	return {
		...toConvergeProduct(item),
		quantity: item.quantity,
		discount: lineDiscount(item),
		sku: item.merchandise.sku ?? undefined,
		image_url: item.merchandise.image?.url
	}
}

export function toConvergeProduct(line: Pick<AddToCartLine, 'id' | 'product' | 'title' | 'price' | 'image'> | Variant) {
	return {
		product_id: gidToId(line.id).toString(),
		variant_id: line.id,
		name: line.product?.title,
		variant_name: line.title,
		price: Number.parseFloat(line.price?.amount ?? '0'),
		currency: line.price?.currencyCode ?? 'EUR',
		image_url: 'image' in line && line.image ? line.image.url : undefined
	}
}

export interface TrackArgs<T extends ConvergeEventSpec['eventName']> extends TrackArgsBase {
	eventName: T
	properties: Extract<ConvergeEventSpec, { eventName: T }>['properties']
}

export type ConvergeEventSpec =
	| _EventSpecBase<'$page_load', ViewedPage>
	| _EventSpecBase<'Viewed Product', ViewedProduct>
	| _EventSpecBase<'Selected Product', SelectedProduct>
	| _EventSpecBase<'Viewed Collection', ViewedCollection>
	| _EventSpecBase<'Added To Cart', AddedToCart>
	| _EventSpecBase<'Removed From Cart', RemovedFromCart>
	| _EventSpecBase<'Added To Wishlist', AddedToWishlist>
	| _EventSpecBase<'Removed From Wishlist', RemovedFromWishlist>
	| _EventSpecBase<'Searched', Searched>
	| _EventSpecBase<'Searched For Store', SearchedForStore>
	| _EventSpecBase<'Logged In', LoggedIn>
	| _EventSpecBase<'Signed Up', SignedUp>
	| _EventSpecBase<'Subscribed To Newsletter', SubscribedToNewsletter>
	| _EventSpecBase<'Viewed Cart', ViewedCart>
	| _EventSpecBase<'Started Checkout', StartedCheckout>
	| _EventSpecBase<'Added Payment Info', AddedPaymentInfo>
	| _EventSpecBase<'Placed Order', PlacedOrder>
	// Custom events
	| _EventSpecBase<'Customize Product', ViewedProduct>
